import React from 'react';
import EmailAutocomplete from 'components/canvas/components/top-left-menu/components/share-surface-button/components/share-surface-modal/components/email-autocomplete';
import Button from 'components/button';
import Form from 'components/form';
import If from 'components/if-statement';
import { useTranslation } from 'i18n';
import { useSubSurfacesCount } from 'components/canvas/components/top-left-menu/components/share-surface-button/components/share-surface-modal/hooks/use-sub-surfaces-count';
import useCreateValidationSchema from 'hooks/use-create-validation-schema';
import useShareSurfaceWithUsers from 'components/canvas/components/top-left-menu/components/share-surface-button/components/share-surface-modal/hooks/use-share-surface-with-users';
import {
  Container,
  Header,
  Section,
  Title,
  OptionLink,
  OptionText,
} from './style';
import { Column, Row, VerticalGap } from 'components/layout';
import { isUndefined, isEmpty } from 'lodash';
import UsersWithAccessList from 'components/canvas/components/top-left-menu/components/share-surface-button/components/share-surface-modal/components/users-with-access-list';
import { ModalContext } from 'hoc/with-modal';
import { array, object, string } from 'yup';
import toast from 'services/toast';

export interface InvitationFormProps {
  modal: ModalContext;
  data: any;
  onSubmitSuccess: Function;
}

const defaultProps = {};

function createValidationSchema(t) {
  return object().shape({
    sharedWith: array().of(
      object().shape({
        email: string()
          .email(t('Emails contain invalid email format'))
          .required(t('At Last one email address is required')),
      }),
    ),
  });
}

function InvitationForm(props: InvitationFormProps) {
  const { t } = useTranslation();

  const validationSchema = useCreateValidationSchema(createValidationSchema);

  const shareSurfaceWithUsers = useShareSurfaceWithUsers();

  const surface = props.data;

  const subSurfacesCount = useSubSurfacesCount(surface.id);

  async function onSubmit(values) {
    const input = {
      shareSubSurfaces: values.shareSubSurfaces,
      sharedWith: values.sharedWith,
    };

    const result = await shareSurfaceWithUsers(input);

    if (result.isFailure) {
      return toast.error(result.getErrorMessage());
    }

    props.onSubmitSuccess();
    props.modal.hide();
  }

  function optionLabelRenderer() {
    const additionalText = subSurfacesCount
      ? t('this will share a total of {{count}} sub cells', {
          replace: { count: subSurfacesCount },
        })
      : t('this will share every new sub cell created');

    return (
      <OptionText>
        {t('Include sub cells')}
        &nbsp;
        <OptionLink>{additionalText}</OptionLink>
      </OptionText>
    );
  }

  return (
    <Form
      initialValues={{ emails: [], shareSubSurfaces: false }}
      onSubmit={onSubmit}
      validationSchema={validationSchema}>
      {(form) => {
        const isValid =
          Object.values(form.errors).every(isUndefined) &&
          form.touched.sharedWith;

        const isSubmitDisabled = isEmpty(form.values.sharedWith) && !isValid;

        return (
          <Container>
            <Header>
              <Row spaceBetween>
                <Button
                  label={t('Close')}
                  secondary
                  onClick={props.modal.hide}
                />
                <Form.SubmitButton
                  disabled={isSubmitDisabled}
                  label={t('Send invitations')}
                  onClick={form.handleSubmit}
                />
              </Row>
            </Header>
            <Section>
              <Title>{t('Share this cell')}</Title>

              <EmailAutocomplete name="sharedWith" />

              <If condition={isSubmitDisabled === false}>
                <If.Then>
                  <VerticalGap gap={50} />

                  <Form.Option
                    label={optionLabelRenderer}
                    name="shareSubSurfaces"
                  />
                </If.Then>
                <If.Else>
                  <VerticalGap gap={50} />
                  <Column>
                    {surface?.id && <UsersWithAccessList surface={surface} />}
                  </Column>
                </If.Else>
              </If>
            </Section>
          </Container>
        );
      }}
    </Form>
  );
}

InvitationForm.defaultProps = defaultProps;

export default InvitationForm;
